import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const PLATE_TYPES = [
  {
    type: 'Standard Plates',
    icon: 'ri-car-line',
    desc: 'Regular Nevada license plates for all registered vehicles.',
    docs: ['Current vehicle registration', 'Valid Nevada ID', 'Proof of insurance', 'Payment for fees'],
    badge: 'Most Common',
    badgeColor: 'bg-[#003366] text-white',
  },
  {
    type: 'Personalized Plates',
    icon: 'ri-edit-line',
    desc: 'Custom plates with your chosen message — up to 7 characters.',
    docs: ['Personalized plate application', 'Valid Nevada ID', 'Current registration', 'Additional specialty fee'],
    badge: 'Popular',
    badgeColor: 'bg-[#F5A623] text-white',
  },
  {
    type: 'Specialty Plates',
    icon: 'ri-star-line',
    desc: 'Special design plates supporting causes, organizations, or interests.',
    docs: ['Specialty plate application', 'Organization documentation (if required)', 'Valid Nevada ID', 'Specialty fee payment'],
    badge: 'Unique Designs',
    badgeColor: 'bg-green-600 text-white',
  },
  {
    type: 'Disabled Plates',
    icon: 'ri-wheelchair-line',
    desc: 'Special plates granting disabled parking privileges.',
    docs: ['Medical certification form', 'Physician signature', 'Valid Nevada ID', 'Current registration'],
    badge: 'Accessibility',
    badgeColor: 'bg-slate-600 text-white',
  },
];

const SERVICES = [
  { icon: 'ri-add-circle-line', title: 'New Plate Issuance', desc: 'Get brand-new Nevada license plates for your vehicle. Standard plates issued same day.', time: 'Same Day' },
  { icon: 'ri-refresh-line', title: 'Plate Replacement', desc: 'Replace lost, stolen, or damaged plates quickly. We handle all replacement paperwork.', time: 'Immediate' },
  { icon: 'ri-exchange-line', title: 'Plate Transfer', desc: 'Transfer your existing plates to a different vehicle. Eligibility verified on-site.', time: 'Same Day' },
  { icon: 'ri-shopping-cart-line', title: 'Specialty Orders', desc: 'Order special design or cause plates. Production takes 2–4 weeks after submission.', time: '2–4 Weeks' },
];

const STEPS = [
  { step: '01', icon: 'ri-phone-line', title: 'Call & Schedule', desc: 'Call 702.979.7202 to book your appointment and confirm which plate type you need.' },
  { step: '02', icon: 'ri-file-list-3-line', title: 'Gather Documents', desc: 'Bring your registration, valid ID, proof of insurance, and any specialty forms.' },
  { step: '03', icon: 'ri-store-2-line', title: 'Visit Our Office', desc: 'Come to 4525 S Sandhill Rd. Our team processes everything while you wait.' },
  { step: '04', icon: 'ri-award-line', title: 'Drive Away', desc: 'Standard plates issued immediately. Specialty plates mailed within 2–4 weeks.' },
];

export default function LicensePlatesPage() {
  const [openType, setOpenType] = useState<number | null>(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada License Plates Las Vegas - Standard, Specialty & Personalized Plates',
    description: 'Complete Nevada license plate services in Las Vegas. Standard, personalized, specialty, and disabled parking plates. Immediate issuance available. Licensed DMV provider. Call 702.979.7202.',
    keywords: 'Nevada license plates Las Vegas, personalized plates Nevada, specialty license plates Las Vegas, Nevada plate replacement',
    canonical: `${siteUrl}/license-plates`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema('License Plate Services', 'Complete Nevada license plate services including standard, specialty, personalized, and replacement plates in Las Vegas.'),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }, { name: 'License Plates', url: '/license-plates' }])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[400px] md:h-[460px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/c41e8056f50466e55b643a74caad18f2.jpg"
            alt="Nevada License Plates"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">License Plates</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl">
              License Plates <span className="text-[#F5A623]">Las Vegas</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl mb-7">
              Standard, personalized, specialty, and replacement plates — all handled in one visit. Most plates issued same day.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-xs"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '$120–$150 + DMV', label: 'Service Fee' },
              { value: 'Same Day', label: 'Standard Plates' },
              { value: '4 Types', label: 'Plate Options' },
              { value: 'All Vehicles', label: 'Types Handled' },
            ].map((s, i) => (
              <div key={i} className="py-4 px-6 text-center">
                <p className="text-[#F5A623] text-lg font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">How It Works</h2>
          <p className="text-gray-500 mb-10 pl-5">Four simple steps — we handle the complexity so you don't have to.</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/15"></div>
            {STEPS.map((item, i) => (
              <div key={i} className="text-center relative group">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATE TYPES ACCORDION */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Nevada Plate Types</h2>
          <p className="text-gray-500 mb-8 pl-5">Select a plate type to see required documents and details.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {PLATE_TYPES.map((t, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openType === i ? 'border-[#003366] shadow-md' : 'border-gray-200 hover:border-[#003366]/40'}`}
                onClick={() => setOpenType(openType === i ? null : i)}
              >
                <div className={`flex items-center justify-between px-5 py-4 ${openType === i ? 'bg-[#003366]' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded flex-shrink-0 ${openType === i ? 'bg-[#F5A623]' : 'bg-[#003366]'}`}>
                      <i className={`${t.icon} ${openType === i ? 'text-white' : 'text-[#F5A623]'} text-base`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className={`font-bold text-sm ${openType === i ? 'text-white' : 'text-[#003366]'}`}>{t.type}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.badgeColor}`}>{t.badge}</span>
                      </div>
                      <p className={`text-xs ${openType === i ? 'text-white/70' : 'text-gray-400'}`}>{t.desc}</p>
                    </div>
                  </div>
                  <i className={`${openType === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-xl flex-shrink-0`}></i>
                </div>
                {openType === i && (
                  <div className="px-5 py-4 bg-white border-t border-gray-100">
                    <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Required Documents</p>
                    <ul className="space-y-2">
                      {t.docs.map((doc, j) => (
                        <li key={j} className="flex items-center space-x-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                        <i className="fas fa-phone text-xs"></i>
                        <span>Call to Confirm Documents</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Plate Services We Offer</h2>
          <p className="text-gray-500 mb-10 pl-5">From new issuance to replacements — we handle every plate need.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <div key={i} className="group flex flex-col p-5 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded mb-4 group-hover:bg-[#F5A623] transition-colors flex-shrink-0">
                  <i className={`${s.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <h3 className="font-bold text-[#003366] text-sm mb-1">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed flex-1">{s.desc}</p>
                <div className="mt-3 inline-flex items-center space-x-1.5">
                  <i className="ri-time-line text-[#F5A623] text-xs"></i>
                  <span className="text-xs font-semibold text-[#003366]">{s.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONALIZED PLATE GUIDELINES */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Personalized Plate Guidelines</h2>
          <p className="text-gray-500 mb-10 pl-5">Know the rules before you apply for a custom plate combination.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Allowed',
                icon: 'ri-check-line',
                color: 'bg-green-600',
                items: ['Letters A–Z', 'Numbers 0–9', 'Spaces (count as characters)', 'Up to 7 characters total'],
              },
              {
                title: 'Not Allowed',
                icon: 'ri-close-line',
                color: 'bg-red-600',
                items: ['Offensive or vulgar language', 'Special characters or symbols', 'Already-taken combinations', 'Reserved state sequences'],
              },
              {
                title: 'Pro Tips',
                icon: 'ri-lightbulb-line',
                color: 'bg-[#F5A623]',
                items: ['Check availability before applying', 'Have 2–3 backup options ready', 'Consider readability at speed', 'Review carefully before ordering'],
              },
            ].map((col, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className={`${col.color} px-5 py-3 flex items-center space-x-2`}>
                  <i className={`${col.icon} text-white text-base`}></i>
                  <span className="text-white font-bold text-sm">{col.title}</span>
                </div>
                <ul className="p-5 space-y-2.5">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-start space-x-2 text-sm text-gray-700">
                      <i className="ri-arrow-right-s-line text-[#F5A623] flex-shrink-0 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEES & TIMELINE */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#003366] px-6 py-4">
                <h3 className="text-white font-bold text-base flex items-center space-x-2">
                  <i className="ri-money-dollar-circle-line text-[#F5A623]"></i>
                  <span>Fee Breakdown</span>
                </h3>
              </div>
              <div className="p-6 space-y-3">
                {[
                  { label: 'Our Service Fee', value: 'Call for Quote' },
                  { label: 'Standard Plate Fee (DMV)', value: '$8.00' },
                  { label: 'Personalized Plate Fee', value: '$36.00/year' },
                  { label: 'Specialty Plate Fee', value: 'Varies by design' },
                  { label: 'Replacement Plate Fee', value: '$8.00' },
                ].map((row, i) => (
                  <div key={i} className={`flex justify-between items-center py-2.5 ${i < 4 ? 'border-b border-gray-100' : ''}`}>
                    <span className="text-gray-600 text-sm">{row.label}</span>
                    <span className="font-bold text-[#003366] text-sm">{row.value}</span>
                  </div>
                ))}
                <div className="mt-4 bg-[#FFF8E7] border border-[#F5A623]/30 rounded p-3">
                  <p className="text-xs text-[#7a5c00]"><strong>Note:</strong> Exact fees depend on vehicle type and transaction details. Call us for a precise quote.</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#003366] px-6 py-4">
                <h3 className="text-white font-bold text-base flex items-center space-x-2">
                  <i className="ri-time-line text-[#F5A623]"></i>
                  <span>Processing Timeline</span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {[
                  { phase: 'Standard Plates', time: 'Same Day', icon: 'ri-car-line', note: 'Issued immediately at our office' },
                  { phase: 'Personalized Plates', time: '2–4 Weeks', icon: 'ri-edit-line', note: 'Produced and mailed by Nevada DMV' },
                  { phase: 'Specialty Plates', time: '2–4 Weeks', icon: 'ri-star-line', note: 'Production and delivery by Nevada DMV' },
                  { phase: 'Replacement Plates', time: 'Same Day', icon: 'ri-refresh-line', note: 'Immediate replacement at our office' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                      <i className={`${item.icon} text-[#F5A623] text-sm`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="font-bold text-[#003366] text-sm">{item.phase}</p>
                        <span className="text-[#F5A623] text-xs font-bold bg-[#FFF8E7] px-2 py-0.5 rounded">{item.time}</span>
                      </div>
                      <p className="text-gray-400 text-xs">{item.note}</p>
                    </div>
                  </div>
                ))}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white py-3 rounded font-bold text-sm transition-colors whitespace-nowrap">
                    <i className="ri-calendar-check-line"></i>
                    <span>Schedule Your Appointment</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Ready to Get Your <span className="text-[#F5A623]">License Plates?</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Whether you need standard, personalized, or specialty plates — our licensed team handles everything fast and accurately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['Licensed 3rd Party Provider', 'Same-Day Standard Plates', 'All Plate Types', 'Transparent Pricing'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
